<template>
  <Teleport to="body">
    <div v-if="ui.profileCardOpen" class="upc-mask" @click.self="close">
      <div class="upc-card" :style="cardStyle">
        <button class="upc-close" type="button" aria-label="关闭" @click="close">✕</button>

        <div v-if="loading" class="upc-loading">加载中…</div>
        <template v-else-if="profile">
          <div class="upc-head">
            <img class="upc-avatar" :data-imagebed-src="avatar" alt="" />
            <div class="upc-id">
              <div class="upc-name">
                {{ profile.username }}
                <span class="lvl-badge" :style="{ background: levelColor }">Lv{{ profile.level || 1 }}</span>
              </div>
              <div class="upc-joined">注册于 {{ joinDate }}</div>
            </div>
          </div>

          <p v-if="profile.bio" class="upc-bio">{{ profile.bio }}</p>
          <LinkText v-if="profile.signature" tag="div" class="upc-sig" :text="profile.signature" />

          <div class="upc-stats">
            <div class="upc-stat"><strong>{{ formatCount(profile.post_count) }}</strong><span>发帖</span></div>
            <div class="upc-stat"><strong>{{ formatCount(profile.comment_count) }}</strong><span>评论</span></div>
            <div class="upc-stat"><strong>{{ formatCount(profile.follower_count) }}</strong><span>粉丝</span></div>
            <div class="upc-stat"><strong>{{ formatCount(profile.following_count) }}</strong><span>关注</span></div>
          </div>

          <div class="upc-actions">
            <template v-if="profile.is_virtual">
            
            </template>
            <template v-else-if="profile.is_me">
              <button class="btn-ghost upc-btn" @click="goEdit">编辑资料</button>
            </template>
            <template v-else>
              <button
                class="btn-primary upc-btn"
                :class="{ 'is-following': following }"
                :disabled="followLoading"
                @click="onFollow"
              >
                {{ followLoading ? '…' : following ? '已关注' : '+ 关注' }}
              </button>
              <button class="btn-ghost upc-btn" @click="onDm">私信</button>
            </template>
          </div>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { getUserProfile, toggleFollow } from '@/api'
import { useUiStore } from '@/store/ui'
import { useUserStore } from '@/store/user'
import { defaultAvatar } from '@/utils/avatar'
import { formatCount } from '@/utils/format'
import LinkText from '@/components/LinkText.vue'

export default {
  name: 'UserProfileCard',
  components: { LinkText },
  data() {
    return {
      ui: useUiStore(),
      userStore: useUserStore(),
      profile: null,
      loading: false,
      following: false,
      followLoading: false,
    }
  },
  computed: {
    avatar() {
      const p = this.profile
      return p?.avatar || defaultAvatar(p?.username, p?.id)
    },
    levelColor() {
      const palette = ['#94a3b8', '#22c55e', '#06b6d4', '#6366f1', '#a855f7', '#f59e0b', '#ef4444']
      return palette[Math.min((this.profile?.level || 1) - 1, palette.length - 1)]
    },
    joinDate() {
      const t = this.profile?.create_time || ''
      return String(t).slice(0, 10) || '—'
    },
    cardStyle() {
      const a = this.ui.profileCardAnchor
      const vw = (typeof window !== 'undefined' ? window.innerWidth : 1280)
      const vh = (typeof window !== 'undefined' ? window.innerHeight : 800)
      const w = Math.min(340, vw - 32)
      if (!a) return { left: Math.max(16, (vw - w) / 2) + 'px', top: '80px', width: w + 'px' }
      // 水平：优先贴头像右侧，其次左侧，兜底居中
      let left
      if (a.right + w + 24 <= vw) left = a.right + 8
      else if (a.left - w - 24 >= 0) left = a.left - w - 8
      else left = Math.max(16, (vw - w) / 2)
      // 垂直：贴头像顶部，maxHeight 不溢出底部
      const top = Math.max(16, a.top)
      return { left: left + 'px', top: top + 'px', width: w + 'px', maxHeight: (vh - top - 16) + 'px' }
    },
  },
  watch: {
    'ui.profileCardOpen'(open) {
      if (open) this.load()
      else this.profile = null
    },
  },
  created() {
    document.addEventListener('keydown', this.onKeydown)
    window.addEventListener('resize', this.onResize)
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown)
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    formatCount,
    onKeydown(e) { if (e.key === 'Escape' && this.ui.profileCardOpen) this.close() },
    onResize() { /* cardStyle 为计算属性，依赖 window 尺寸时需触发重算 */ this.$forceUpdate() },
    close() { this.ui.closeProfileCard() },
    async load() {
      const author = this.ui.profileCardAuthor
      if (!author || !author.id) return
      // 虚拟身份（导入内容生成的匿名作者）：合成资料，不发请求、不查 /api/u/:id
      if (author.is_virtual) { this.synthesizeVirtual(author); return }
      // 真实用户：拉公开资料 + 统计
      this.loading = true
      this.profile = null
      try {
        const res = await getUserProfile(author.id)
        this.profile = res.data
        this.following = !!res.data?.is_following
      } catch (e) {
        // 404 兜底：前端缓存里的旧 author 对象可能缺 is_virtual 标记（改后端前缓存的匿名帖），
        // 此时 /api/u/:id 查无此真实用户 → 按虚拟身份合成展示，而非报「用户不存在」
        if (e.code === 404) { this.synthesizeVirtual(author); return }
        this.ui.showToast(e.message || '加载失败', 'error')
        this.close()
      } finally {
        this.loading = false
      }
    },
    // 合成虚拟身份资料（匿名/导入作者）：注册时间 = 引用时间 - 2个月5天
    synthesizeVirtual(author) {
      this.profile = {
        id: author.id,
        username: author.username,
        avatar: author.avatar || '',
        bio: '',
        signature: author.signature || '',
        level: author.level || 1,
        create_time: this.virtualRegisterTime(),
        post_count: 1,
        comment_count: 1,
        follower_count: 1,
        following_count: 0,
        is_following: false,
        is_me: false,
        is_virtual: true,
      }
      this.following = false
      this.loading = false
    },
    // 虚拟用户注册时间 = 引用时间(帖子/评论创建时间) - 2个月5天
    virtualRegisterTime() {
      const ref = this.ui.profileCardRefTime
      if (!ref) return ''
      const d = new Date(String(ref).replace(/-/g, '/'))   // Safari 兼容
      if (isNaN(d.getTime())) return String(ref).slice(0, 10)
      d.setMonth(d.getMonth() - 2)
      d.setDate(d.getDate() - 5)
      const pad = (n) => String(n).padStart(2, '0')
      return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
    },
    async onFollow() {
      if (!this.userStore.isLogin) { this.$router.push('/login'); return }
      if (!this.profile) return
      this.followLoading = true
      const wasFollowing = this.following
      try {
        const res = await toggleFollow(this.profile.id)
        this.following = !!res.data?.is_following
        this.profile.follower_count = res.data?.follower_count ?? this.profile.follower_count
        if (this.following && !wasFollowing) this.ui.showToast('已关注', 'success')
      } catch (e) {
        this.ui.showToast(e.message || '操作失败', 'error')
      } finally {
        this.followLoading = false
      }
    },
    onDm() {
      if (!this.userStore.isLogin) { this.$router.push('/login'); return }
      const p = this.profile
      this.ui.openDmModal({ id: p.id, username: p.username, avatar: this.avatar })
      this.close()
    },
    goEdit() {
      this.close()
      this.$router.push('/me')
    },
  },
}
</script>

<style scoped>
.upc-mask { position: fixed; inset: 0; z-index: 180; }
.upc-card {
  position: fixed; overflow-y: auto;
  background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg); padding: 18px 18px 16px;
}
.upc-close {
  position: absolute; top: 10px; right: 10px; width: 26px; height: 26px; border-radius: 6px;
  border: none; background: none; color: var(--text-3); font-size: 14px; cursor: pointer; line-height: 1;
}
.upc-close:hover { background: var(--surface-2); color: var(--text); }
.upc-loading { padding: 28px 0; text-align: center; color: var(--text-3); font-size: 13px; }
.upc-head { display: flex; align-items: center; gap: 12px; }
.upc-avatar { width: 56px; height: 56px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
.upc-id { min-width: 0; }
.upc-name { font-size: 16px; font-weight: 800; display: flex; align-items: center; gap: 8px; }
.lvl-badge { font-size: 11px; font-weight: 700; color: #fff; padding: 2px 7px; border-radius: 999px; }
.upc-joined { font-size: 12px; color: var(--text-3); margin-top: 4px; }
.upc-bio { font-size: 13px; color: var(--text-2); line-height: 1.6; margin-top: 12px; word-break: break-word; }
.upc-sig { font-size: 12px; color: var(--text-3); margin-top: 8px; padding-top: 8px; border-top: 1px dashed var(--border); line-height: 1.5; }
.upc-sig a { color: var(--primary); }
.upc-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; margin-top: 14px; }
.upc-stat { display: flex; flex-direction: column; align-items: center; gap: 2px; padding: 8px 4px; background: var(--surface-2); border-radius: var(--radius-sm); }
.upc-stat strong { font-size: 15px; font-weight: 800; color: var(--text); }
.upc-stat span { font-size: 11px; color: var(--text-3); }
.upc-actions { display: flex; gap: 8px; margin-top: 14px; }
.upc-btn { flex: 1; height: 36px; justify-content: center; }
.upc-btn.is-following { background: var(--surface-2); color: var(--text-2); border: 1px solid var(--border); }
.upc-virtual-note { flex: 1; text-align: center; font-size: 12px; color: var(--text-3); background: var(--surface-2); border-radius: var(--radius-sm); padding: 8px 10px; }
</style>
